import { useMemo, useState } from 'react';
import {
  Modal, View, Text, TextInput, TouchableOpacity, ScrollView,
  StyleSheet, Linking, Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMessageTemplates, MessageTemplate, TemplateCategory } from '@/hooks/useMessageTemplates';
import { useNotificationLog } from '@/hooks/useNotificationLog';
import { StudentWithRetention } from '@/hooks/useRetention';
import {
  RetentionCategory, CATEGORY_LABEL, CATEGORY_COLOR,
} from '@/lib/retention';

interface Props {
  visible: boolean;
  onClose: () => void;
  personalId: string;
  preselected: StudentWithRetention[];
  initialCategory?: RetentionCategory | 'todos';
}

const DAY_MS = 86_400_000;

function firstName(s: StudentWithRetention): string {
  return (s.full_name ?? s.email ?? 'aluno').split(' ')[0];
}

function resolvePlaceholders(body: string, student: StudentWithRetention): string {
  const dias = student.daysSinceLast ?? 0;
  return body
    .replace(/\{nome\}/g, firstName(student))
    .replace(/\{dias_sem_treinar\}/g, String(dias))
    .replace(/\{treinos_semana\}/g, String(student.sessionsLastWeek))
    .replace(/\{mensagem\}/g, ''); // placeholder vazio se não substituído pelo PT
}

function cleanPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return null;
  // Se nao tiver DDI, assume 55 (Brasil)
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
}

export function NotifyModal({ visible, onClose, personalId, preselected, initialCategory }: Props) {
  const { templates } = useMessageTemplates(personalId);
  const { logNotification } = useNotificationLog(personalId);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(preselected.map(s => s.id)));
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [messageBody, setMessageBody] = useState<string>('');
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());

  // Filtra templates pela categoria atual (se houver) + geral
  const categoryForFilter: TemplateCategory = useMemo(() => {
    if (initialCategory && initialCategory !== 'todos') return initialCategory;
    return 'geral';
  }, [initialCategory]);

  const visibleTemplates = useMemo(() => {
    return templates.filter(t =>
      t.category === categoryForFilter || t.category === 'geral'
    );
  }, [templates, categoryForFilter]);

  function pickTemplate(t: MessageTemplate) {
    setTemplateId(t.id);
    setMessageBody(t.body);
  }

  function toggleStudent(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function sendToStudent(student: StudentWithRetention) {
    const phone = cleanPhone(student.phone);
    if (!phone) {
      Alert.alert('Sem telefone', `${firstName(student)} não tem telefone cadastrado.`);
      return;
    }
    const text = resolvePlaceholders(messageBody, student);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    Linking.openURL(url).catch(() => {
      Alert.alert('Falha', 'Não foi possível abrir o WhatsApp.');
    });
    setSentIds(prev => new Set(prev).add(student.id));
  }

  async function finalize() {
    if (sentIds.size === 0) {
      onClose();
      return;
    }
    try {
      await logNotification({
        student_ids: [...sentIds],
        category: initialCategory && initialCategory !== 'todos' ? initialCategory : null,
        template_id: templateId,
        message_text: messageBody,
        channel: 'whatsapp',
      });
    } catch {
      // log falhou — segue silencioso
    }
    setSentIds(new Set());
    onClose();
  }

  const selectedList = preselected.filter(s => selectedIds.has(s.id));
  const canSend = messageBody.trim().length > 0;

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={styles.screen}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close" size={26} color="#1A1A1A" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Notificar alunos</Text>
          <View style={{ width: 26 }} />
        </View>

        <ScrollView contentContainerStyle={styles.content}>

          {/* ─── Templates ─── */}
          <Text style={styles.sectionLabel}>Modelo de mensagem</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.templatesRow}>
            {visibleTemplates.length === 0 && (
              <Text style={styles.emptyTemplates}>Nenhum modelo disponível</Text>
            )}
            {visibleTemplates.map(t => (
              <TouchableOpacity
                key={t.id}
                style={[styles.templateChip, templateId === t.id && styles.templateChipActive]}
                onPress={() => pickTemplate(t)}
                activeOpacity={0.7}
              >
                <Text style={[styles.templateChipText, templateId === t.id && styles.templateChipTextActive]}>
                  {t.name}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* ─── Mensagem ─── */}
          <Text style={[styles.sectionLabel, { marginTop: 16 }]}>Mensagem</Text>
          <TextInput
            style={styles.textArea}
            value={messageBody}
            onChangeText={setMessageBody}
            multiline
            placeholder="Escreva a mensagem ou escolha um modelo acima…"
            placeholderTextColor="#aaa"
          />
          <Text style={styles.placeholdersHint}>
            Pode usar: {'{nome}'}, {'{dias_sem_treinar}'}, {'{treinos_semana}'}, {'{mensagem}'}
          </Text>

          {/* ─── Alunos ─── */}
          <Text style={[styles.sectionLabel, { marginTop: 16 }]}>
            Alunos selecionados ({selectedList.length}/{preselected.length})
          </Text>
          {preselected.length === 0 ? (
            <Text style={styles.emptyStudents}>Nenhum aluno na seleção.</Text>
          ) : (
            preselected.map(s => {
              const selected = selectedIds.has(s.id);
              const sent = sentIds.has(s.id);
              const noPhone = !cleanPhone(s.phone);
              return (
                <View key={s.id} style={styles.studentRow}>
                  <TouchableOpacity onPress={() => toggleStudent(s.id)} style={styles.checkbox} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons
                      name={selected ? 'checkbox' : 'square-outline'}
                      size={22}
                      color={selected ? '#C8102E' : '#aaa'}
                    />
                  </TouchableOpacity>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.studentName} numberOfLines={1}>
                      {s.full_name ?? s.email}
                    </Text>
                    <View style={styles.studentMetaRow}>
                      <View style={[styles.catTag, { backgroundColor: CATEGORY_COLOR[s.category] + '22' }]}>
                        <Text style={[styles.catTagText, { color: CATEGORY_COLOR[s.category] }]}>
                          {CATEGORY_LABEL[s.category]}
                        </Text>
                      </View>
                      {noPhone && <Text style={styles.warnText}>sem telefone</Text>}
                    </View>
                  </View>
                  <TouchableOpacity
                    style={[styles.sendBtn, (sent || !selected || !canSend || noPhone) && styles.sendBtnDisabled]}
                    onPress={() => sendToStudent(s)}
                    disabled={sent || !selected || !canSend || noPhone}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="logo-whatsapp" size={16} color={sent ? '#aaa' : '#fff'} />
                    <Text style={[styles.sendBtnText, sent && styles.sendBtnTextDisabled]}>
                      {sent ? 'Enviado' : 'Enviar'}
                    </Text>
                  </TouchableOpacity>
                </View>
              );
            })
          )}
        </ScrollView>

        {/* ─── Footer ─── */}
        <View style={styles.footer}>
          <Text style={styles.footerCount}>
            {sentIds.size > 0 ? `${sentIds.size} aluno${sentIds.size > 1 ? 's' : ''} notificado${sentIds.size > 1 ? 's' : ''}` : 'Nenhum envio ainda'}
          </Text>
          <TouchableOpacity style={styles.doneBtn} onPress={finalize} activeOpacity={0.7}>
            <Text style={styles.doneBtnText}>
              {sentIds.size > 0 ? 'Concluir e registrar' : 'Fechar'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#F5F5F5' },

  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingTop: 16, paddingBottom: 12,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#EEE',
  },
  headerTitle: { fontSize: 16, fontWeight: '700', color: '#1A1A1A' },

  content: { padding: 16, paddingBottom: 40 },

  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#888', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 },

  templatesRow: { gap: 8, paddingVertical: 2 },
  templateChip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 16, backgroundColor: '#fff', borderWidth: 1, borderColor: '#DDD' },
  templateChipActive: { backgroundColor: '#C8102E', borderColor: '#C8102E' },
  templateChipText: { fontSize: 12, color: '#555', fontWeight: '600' },
  templateChipTextActive: { color: '#fff' },
  emptyTemplates: { fontSize: 12, color: '#888', fontStyle: 'italic', padding: 8 },

  textArea: {
    backgroundColor: '#fff', borderRadius: 10, padding: 12, fontSize: 14,
    color: '#1A1A1A', borderWidth: 1, borderColor: '#EEE',
    minHeight: 100, textAlignVertical: 'top',
  },
  placeholdersHint: { fontSize: 11, color: '#888', marginTop: 6, fontStyle: 'italic' },

  emptyStudents: { fontSize: 13, color: '#888', textAlign: 'center', paddingVertical: 20 },
  studentRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#fff', padding: 12, borderRadius: 10, marginBottom: 6,
  },
  checkbox: { paddingRight: 4 },
  studentName: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  studentMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
  catTag: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  catTagText: { fontSize: 10, fontWeight: '700' },
  warnText: { fontSize: 10, color: '#C8102E', fontStyle: 'italic' },

  sendBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#25D366', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8,
  },
  sendBtnDisabled: { backgroundColor: '#E0E0E0' },
  sendBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  sendBtnTextDisabled: { color: '#aaa' },

  footer: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 16, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#EEE',
    gap: 10,
  },
  footerCount: { fontSize: 12, color: '#555', flex: 1 },
  doneBtn: { backgroundColor: '#C8102E', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8 },
  doneBtnText: { color: '#fff', fontWeight: '700', fontSize: 13 },
});
